/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  dbFileToTableEntry,
  dbFolderToTableEntry,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { idOrNull } from '~src/services/api';
import { DB_STATE } from '~src/types/enums';
import { List, Avatar, message } from 'antd';
import { FileListItem } from '~components/atoms/mobile/FileListItem/FileListItem';
import { downloadFileLastVersion } from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { appLog } from '~src/utils/log';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';

interface FileListProps {
  test?: boolean;
}

export const FileList: React.FC<FileListProps> = ({}) => {
  const currentFolder = useCurrentFolder();
  const history = useHistory();
  const intl = useIntl();
  const projectId = useProjectId();
  const folderId = currentFolder?.id || null;
  const styles = useStyles(styleFn);

  //contexts
  const { fileVersionsContext, setFileVersionsContext } = useContext(
    FileVersionsContext
  );

  const searchContext = useContext(SearchContext);
  const { setFileToView } = useFileViewerContext();

  //queryies
  const foldersQuery = useFolders({
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const filesQuery = useFiles(
    {
      folderId: idOrNull(folderId),
      withVersions: true,
      state: [DB_STATE.AVAILABLE],
    },
    {
      enabled: !!currentFolder,
      onError: () =>
        // go to /archive
        // history.replace(`/projects/${projectId}/archive`),
        appLog('error here => '),
    }
  );

  //sources
  const foldersSource = useMemo(
    () =>
      (foldersQuery.data || [])
        .filter((v) => v.parent_id === folderId)
        .map(dbFolderToTableEntry),
    [foldersQuery.data, folderId]
  );
  const filesSource = useMemo(
    () =>
      (filesQuery.data || [])
        .filter((item) => item.state !== DB_STATE.PARTIAL)
        .map((item) => dbFileToTableEntry(item)),
    [filesQuery.data]
  );

  React.useEffect(() => {
    const files = filesQuery.data || [];
    if (!Array.isArray(files)) return;

    const map: Record<string, any> = {};
    for (const f of files) {
      const v = f?.versions?.[0];
      if (f?.id && v) {
        map[f.id] = v;
      }
    }

    const keysA = Object.keys(fileVersionsContext || {});
    const keysB = Object.keys(map);
    const sameShape =
      keysA.length === keysB.length && keysA.every((k) => map[k]);

    if (!sameShape) {
      setFileVersionsContext(map);
    }
  }, [filesQuery.data, setFileVersionsContext]);

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];
  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const handleFolderClick = (record: TableEntry) => {
    // window.clearTimeout(clickTimeoutRef.current);
    // clickTimeoutRef.current = undefined;
    history.push(`/projects/${projectId}/archive/${record.key}`);
  };

  const handleFileClick = (record: TableEntry) => {
    if (!record.dbFile) return;

    // Detect 3D + viewability
    const ext = (
      record.extension ||
      record.dbFile.title.split('.').pop() ||
      ''
    ).toLowerCase();
    const isCad = ['rvt', 'dwg', 'ifc', 'nwd'].includes(ext);

    const version =
      fileVersionsContext[record.id] ?? record.dbFile.versions?.[0];
    const isViewable = version?.extension_svf_model?.state === 7;

    // If 3D and transcribed -> open viewer; else -> download
    if (isCad && isViewable) {
      setFileToView({ ...record.dbFile });
      return;
    }

    // Default download logic if file is not cad and viewable
    const hide = message.loading(
      intl.formatMessage({ id: 'download-starting' }),
      0
    );
    downloadFileLastVersion(record.dbFile, fileVersionsContext)
      .catch((err) => {
        appLog('downloadFile -> err', err);
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      })
      .finally(() => hide());
  };

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  return (
    <div css={styles.container}>
      <List<TableEntry>
        dataSource={getTableData()}
        css={{ width: '100%', paddingBottom: 50 }}
        loading={filesQuery.isLoading || foldersQuery.isLoading}
        renderItem={(item) => (
          <FileListItem
            item={item}
            onClick={
              item.folder
                ? () => handleFolderClick(item)
                : () => handleFileClick(item)
            }
          />
        )}
      />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    height: '100%',
    width: '100%',
    // paddingBottom: 50,
  },
});
